import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import Navbarsticky from "./NavbarSticky";
import Footer from "./Footer";

import {
  bookTravelPackage,
  bookWaterSports,
  clearBooking,
  verifyPayment,
} from "../store/slices/booking";

const Checkout = () => {
  const [isPaying, setIsPaying] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState("FULL");

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  const rzpOpenedRef = useRef(false); // ✅ FIX

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { isLoggedIn, user } = useSelector((state) => state.auth);
  const { error } = useSelector((state) => state.booking);

  const {
    bookingType = "TRAVEL_PACKAGE",
    travelPackageId,
    waterSportId,
    travelDate,
    pax,
    pricing,
    meta = {},
  } = location.state || {};

  /* ---------------- GUARDS ---------------- */
  useEffect(() => {
    if (!location.state) navigate("/");
  }, [location.state, navigate]);

  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.setItem("redirectAfterLogin", "/checkout");
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    return () => dispatch(clearBooking());
  }, [dispatch]);

  if (!pricing || !pax) return null;

  const payableNow =
    paymentPlan === "PARTIAL"
      ? Math.round(pricing.amountPayable * 0.3)
      : pricing.amountPayable;

  /* ---------------- MAKE PAYMENT ---------------- */
  const handleMakePayment = async () => {
    if (isPaying) return;

    try {
      setIsPaying(true);
      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        alert("Razorpay failed to load");
        setIsPaying(false);
        return;
      }

      const basePayload = {
        ...(bookingType === "WATER_SPORT"
          ? { waterSportId }
          : { travelPackageId }),
        ...(bookingType === "WATER_SPORT"
          ? { bookingDate: travelDate }
          : { travelDate }),
        pax,
        pricing,
        paymentPlan,
        contact: {
          name: user?.name,
          email: user?.email,
          phone: user?.mobileNumber,
        },
        placeName: meta.placeName,
        timeSlot: meta.timeSlot,
        meta,
      };

      const action =
        bookingType === "WATER_SPORT" ? bookWaterSports : bookTravelPackage;

      const data = await dispatch(action(basePayload)).unwrap();

      openRazorpay(data?.razorpay);
    } catch (err) {
      console.error("Booking error:", err);
      setIsPaying(false);
    }
  };

  /* ---------------- RAZORPAY ---------------- */
  const openRazorpay = (rzpData) => {
    if (!rzpData || rzpOpenedRef.current) return; // 🔴 FIX

    rzpOpenedRef.current = true;

    const options = {
      key: rzpData.keyId,
      amount: rzpData.amount,
      currency: rzpData.currency,
      order_id: rzpData.orderId,

      name: "Andaman Make Holidays",
      description:
        bookingType === "WATER_SPORT"
          ? "Water Sports Booking"
          : "Travel Package Booking",

      handler: async (response) => {
        try {
          await dispatch(
            verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            }),
          ).unwrap();

          setIsPaying(false);
          rzpOpenedRef.current = false;

          navigate("/booking-history");
        } catch (err) {
          alert("Payment done, but verification failed");
          setIsPaying(false);
          rzpOpenedRef.current = false;
        }
      },

      modal: {
        ondismiss: () => {
          setIsPaying(false);
          rzpOpenedRef.current = false; // ✅ reset if closed
        },
      },

      theme: { color: "#0d6efd" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      <Navbarsticky />
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="shadow-sm rounded-4 border-0 p-4 position-relative">
              {isPaying && (
                <div className="overlay-loader">
                  <div className="spinner-border text-primary mb-2" />
                  <div>Processing payment...</div>
                </div>
              )}

              <h4 className="fw-bold mb-4">Checkout</h4>
              {error && <Alert variant="danger">{error}</Alert>}

              {/* SUMMARY */}
              <div className="mb-4">
                <h6 className="fw-bold">Booking Summary</h6>

                {bookingType === "WATER_SPORT" ? (
                  <>
                    <p>
                      <strong>Activity:</strong> {meta.waterSportName}
                    </p>
                    <p>
                      <strong>Place:</strong> {meta.placeName}
                    </p>
                    <p>
                      <strong>Time Slot:</strong> {meta.timeSlot}
                    </p>
                  </>
                ) : (
                  <p>
                    <strong>Travel Date:</strong>{" "}
                    {new Date(travelDate).toDateString()}
                  </p>
                )}

                <p>
                  <strong>Adults:</strong> {pax.adults}
                </p>
                <p>
                  <strong>Children:</strong> {pax.children}
                </p>
              </div>

              <hr />

              {/* PRICE */}
              <div className="mb-4">
                <div className="d-flex justify-content-between">
                  <span>Base Price</span>
                  <span>₹ {pricing.basePrice}</span>
                </div>

                {pricing.discount > 0 && (
                  <div className="d-flex justify-content-between text-success">
                    <span>Discount</span>
                    <span>- ₹ {pricing.discount}</span>
                  </div>
                )}

                {pricing.taxAmount > 0 && (
                  <div className="d-flex justify-content-between">
                    <span>Tax</span>
                    <span>₹ {pricing.taxAmount}</span>
                  </div>
                )}

                <hr />
                <div className="d-flex justify-content-between fw-bold fs-5">
                  <span>Total</span>
                  <span>₹ {pricing.amountPayable}</span>
                </div>
              </div>

              {/* PAYMENT PLAN */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">Payment Option</Form.Label>

                <Form.Check
                  type="radio"
                  name="paymentPlan"
                  label={`Pay Full Amount (₹${pricing.amountPayable})`}
                  checked={paymentPlan === "FULL"}
                  onChange={() => setPaymentPlan("FULL")}
                />

                <Form.Check
                  type="radio"
                  name="paymentPlan"
                  label={`Pay 30% Advance (₹${Math.round(
                    pricing.amountPayable * 0.3,
                  )})`}
                  checked={paymentPlan === "PARTIAL"}
                  onChange={() => setPaymentPlan("PARTIAL")}
                />
              </Form.Group>

              <Alert variant={paymentPlan === "PARTIAL" ? "warning" : "info"}>
                You will pay <strong>₹ {payableNow}</strong> now.
              </Alert>

              <Button
                className="w-100 py-3 fw-bold"
                disabled={isPaying}
                onClick={handleMakePayment}
              >
                MAKE PAYMENT
              </Button>
            </Card>
          </Col>
        </Row>
      </Container>
      <Footer />
    </>
  );
};

export default Checkout;
